import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Thin wrapper over the Web Speech API.
 *
 * Typed locally because `SpeechRecognition` is not in the DOM lib — it is a
 * vendor-prefixed draft, and only Chromium ships it.
 */

interface SpeechRecognitionAlternativeLike {
  transcript: string
}

interface SpeechRecognitionResultLike {
  readonly length: number
  isFinal: boolean
  [index: number]: SpeechRecognitionAlternativeLike
}

interface SpeechRecognitionEventLike extends Event {
  resultIndex: number
  results: {
    readonly length: number
    [index: number]: SpeechRecognitionResultLike
  }
}

interface SpeechRecognitionErrorEventLike extends Event {
  error: string
}

interface SpeechRecognitionLike extends EventTarget {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start: () => void
  stop: () => void
  abort: () => void
  onstart: ((event: Event) => void) | null
  onend: ((event: Event) => void) | null
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function getConstructor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const scope = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null
}

const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': 'Microphone access was blocked.',
  'service-not-allowed': 'The speech service is unavailable.',
  'no-speech': 'No speech was detected.',
  'audio-capture': 'No microphone was found.',
  network: 'The speech service could not be reached.',
}

export interface SpeechRecognitionState {
  isSupported: boolean
  isListening: boolean
  error: string | null
  start: () => void
  stop: () => void
  toggle: () => void
}

export interface UseSpeechRecognitionOptions {
  lang?: string
  /** Fired on every update, interim results included. */
  onTranscript: (transcript: string, isFinal: boolean) => void
  /** Fired once when a final transcript is available. */
  onFinal?: (transcript: string) => void
}

export function useSpeechRecognition({
  lang = 'en-IN',
  onTranscript,
  onFinal,
}: UseSpeechRecognitionOptions): SpeechRecognitionState {
  const [isListening, setIsListening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)

  // Callbacks live in refs so re-renders don't tear down an active session.
  // Written in an effect rather than during render: mutating a ref while
  // rendering is not safe under concurrent rendering.
  const onTranscriptRef = useRef(onTranscript)
  const onFinalRef = useRef(onFinal)

  useEffect(() => {
    onTranscriptRef.current = onTranscript
    onFinalRef.current = onFinal
  }, [onTranscript, onFinal])

  const isSupported = getConstructor() !== null

  useEffect(() => {
    const Ctor = getConstructor()
    if (!Ctor) return

    const recognition = new Ctor()
    recognition.lang = lang
    recognition.continuous = false
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    recognition.onstart = () => {
      setError(null)
      setIsListening(true)
    }

    recognition.onend = () => setIsListening(false)

    recognition.onerror = (event) => {
      setIsListening(false)
      setError(ERROR_MESSAGES[event.error] ?? 'Voice input failed.')
    }

    recognition.onresult = (event) => {
      let transcript = ''
      let isFinal = false

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]
        if (!result) continue
        transcript += result[0]?.transcript ?? ''
        if (result.isFinal) isFinal = true
      }

      const trimmed = transcript.trim()
      if (!trimmed) return

      onTranscriptRef.current(trimmed, isFinal)
      if (isFinal) onFinalRef.current?.(trimmed)
    }

    recognitionRef.current = recognition

    return () => {
      recognition.onstart = null
      recognition.onend = null
      recognition.onerror = null
      recognition.onresult = null
      recognition.abort()
      recognitionRef.current = null
    }
  }, [lang])

  const start = useCallback(() => {
    try {
      setError(null)
      recognitionRef.current?.start()
    } catch {
      // `start()` throws if a session is already running — harmless.
    }
  }, [])

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
  }, [])

  const toggle = useCallback(() => {
    if (isListening) stop()
    else start()
  }, [isListening, start, stop])

  return { isSupported, isListening, error, start, stop, toggle }
}
